/**
 * Instrument Sidebar Component
 * Displays instrument search, watchlist groups, and watchlist items
 * Similar to the left sidebar in trading platforms
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Card,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
  Select,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  ListSubheader,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Psychology as PsychologyIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useInstrumentSearch, useWatchlist, useMarketDataSubscription } from '@shared/features/market-data';
import { useAppSelector, useAppDispatch } from '@shared/store/hooks';
import { registerInstruments } from '@shared/store/slices/marketDataSlice';
import { formatCurrency, formatPercentage } from '@shared/utils/formatters';
import { Instrument } from '@shared/types/instrument';
import { QuickOrderDialog } from './QuickOrderDialog';
import { DraggableInstrumentItem } from './DraggableInstrumentItem';
import { api } from '@shared/api';

interface InstrumentSidebarProps {
  width?: number;
  onInstrumentSelect?: (instrument: Instrument) => void;
}

export function InstrumentSidebar({ width = 350, onInstrumentSelect }: InstrumentSidebarProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<string>('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [searchPage, setSearchPage] = useState(1);
  const [segmentFilter, setSegmentFilter] = useState<string>('EQ');
  const [activeTab, setActiveTab] = useState<'NIFTY50' | 'BANKNIFTY' | 'WATCHLIST'>('WATCHLIST');
  const [nifty50Constituents, setNifty50Constituents] = useState<Instrument[]>([]);
  const [bankniftyConstituents, setBankniftyConstituents] = useState<Instrument[]>([]);
  const [indicesLoading, setIndicesLoading] = useState<boolean>(false);
  const [indicesError, setIndicesError] = useState<string | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const ITEMS_PER_PAGE = 10;

  const handleSegmentChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSegment: string | null,
  ) => {
    if (newSegment !== null) {
      setSegmentFilter(newSegment);
    }
  };

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    instrument: Instrument | null;
  } | null>(null);

  // Item Menu state (unified for search results and watchlist items)
  const [itemMenuAnchorEl, setItemMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuInstrument, setMenuInstrument] = useState<Instrument | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleOpenItemMenu = (event: React.MouseEvent<HTMLElement>, instrument: Instrument) => {
    event.stopPropagation();
    setItemMenuAnchorEl(event.currentTarget);
    setMenuInstrument(instrument);
  };

  const handleCloseItemMenu = (event?: React.MouseEvent | {}, _reason?: "backdropClick" | "escapeKeyDown") => {
    if (event && 'stopPropagation' in event) (event as React.MouseEvent).stopPropagation();
    setItemMenuAnchorEl(null);
  };

  const { search, results, loading, error, clear } = useInstrumentSearch();
  const {
    groups,
    currentGroup,
    addInstrument,
    removeInstrument,
    setGroup,
    createGroup,
    editGroupName,
    deleteGroup,
    isInWatchlist,
    loadGroups,
    loadItems,
    loading: watchlistLoading,
    error: watchlistError,
  } = useWatchlist();
  const instruments = useAppSelector((state) => state.marketData.instruments);
  const liveQuotes = useAppSelector((state) => state.marketData.liveQuotes);
  const watchlistGroups = useAppSelector((state) => state.marketData.watchlistGroups);
  const [addingInstrumentId, setAddingInstrumentId] = useState<string | null>(null);
  const [removingInstrumentId, setRemovingInstrumentId] = useState<string | null>(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState<Instrument | null>(null);
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');

  // Load groups on mount (only if not already loaded)
  useEffect(() => {
    // Only load if groups haven't been loaded yet
    if (groups.length === 0 && !watchlistLoading) {
      loadGroups().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load watchlist groups:', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (activeTab === 'NIFTY50' && nifty50Constituents.length === 0) {
      setIndicesLoading(true);
      setIndicesError(null);
      api.getIndexConstituents('NIFTY 50')
        .then((res) => {
          const constituents = res.data as unknown as Instrument[];
          setNifty50Constituents(constituents);
          dispatch(registerInstruments(constituents));
        })
        .catch((err) => {
          console.error('Failed to load NIFTY 50 constituents', err);
          setIndicesError('Failed to load indices data.');
        })
        .finally(() => {
          setIndicesLoading(false);
        });
    } else if (activeTab === 'BANKNIFTY' && bankniftyConstituents.length === 0) {
      setIndicesLoading(true);
      setIndicesError(null);
      api.getIndexConstituents('BANKNIFTY')
        .then((res) => {
          const constituents = res.data as unknown as Instrument[];
          setBankniftyConstituents(constituents);
          dispatch(registerInstruments(constituents));
        })
        .catch((err) => {
          console.error('Failed to load BANKNIFTY constituents', err);
          setIndicesError('Failed to load indices data.');
        })
        .finally(() => {
          setIndicesLoading(false);
        });
    }
  }, [activeTab, nifty50Constituents.length, bankniftyConstituents.length]);

  // Track which group IDs are currently being fetched to prevent duplicate in-flight requests
  const fetchingGroupIds = React.useRef<Set<string>>(new Set());

  // Load items for the current group when it changes.
  // Uses itemsLoaded flag (not instruments.length) to distinguish
  // "not yet fetched" from "fetched and genuinely empty".
  useEffect(() => {
    if (currentGroup) {
      const group = watchlistGroups[currentGroup];
      if (!group?.itemsLoaded && !fetchingGroupIds.current.has(currentGroup)) {
        // eslint-disable-next-line no-console
        console.log('InstrumentSidebar: Loading items for group (not yet loaded):', currentGroup);
        fetchingGroupIds.current.add(currentGroup);
        loadItems(currentGroup)
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to load watchlist items:', error);
          })
          .finally(() => {
            fetchingGroupIds.current.delete(currentGroup);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup]); // Only depend on currentGroup, not loadItems

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        search(searchQuery, undefined, segmentFilter);
        setSearchPage(1); // Reset to first page when search query changes
      } else {
        clear();
        setSearchPage(1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, segmentFilter, search, clear]);

  const handleClearSearch = () => {
    setSearchQuery('');
    clear();
    setSearchPage(1);
    searchInputRef.current?.focus();
  };

  // Find groups that contain a specific instrument
  const getGroupsContainingInstrument = useCallback((instrumentId: string): string[] => {
    if (!watchlistGroups) return [];
    const groupIds: string[] = [];
    Object.keys(watchlistGroups).forEach((groupId) => {
      const group = watchlistGroups[groupId];
      if (group && group.instruments && group.instruments.includes(instrumentId)) {
        groupIds.push(groupId);
      }
    });
    return groupIds;
  }, [watchlistGroups]);

  // Auto-expand groups containing instruments in search results
  useEffect(() => {
    if (searchQuery.trim() && results && results.length > 0 && watchlistGroups) {
      // Find all groups that contain any of the search results
      const groupsToExpand = new Set<string>();

      results.forEach((instrument) => {
        const containingGroups = getGroupsContainingInstrument(instrument.instrument_id);
        containingGroups.forEach((groupId) => {
          groupsToExpand.add(groupId);
        });
      });

      // Expand all found groups
      if (groupsToExpand.size > 0) {
        setExpandedGroups((prev) => {
          const updated = { ...prev };
          groupsToExpand.forEach((groupId) => {
            updated[groupId] = true;
          });
          return updated;
        });

        // Also load items for these groups if not already loaded
        groupsToExpand.forEach((groupId) => {
          loadItems(groupId).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to load watchlist items:', error);
          });
        });
      }
    }
  }, [results, searchQuery, getGroupsContainingInstrument, loadItems, watchlistGroups]);

  // Ctrl+K keyboard shortcut to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Ctrl+K (or Cmd+K on Mac) is pressed
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        // Prevent default browser behavior (like opening browser search)
        e.preventDefault();
        // Focus the search input
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Get instruments for a specific group
  const getGroupInstruments = useCallback((groupId: string): Instrument[] => {
    if (!watchlistGroups || !watchlistGroups[groupId]) {
      return [];
    }
    const group = watchlistGroups[groupId];
    if (!group || !group.instruments) {
      return [];
    }
    const result = group.instruments
      .map((id) => {
        const inst = instruments[id];
        if (!inst) {
          // Instrument might not be loaded yet, skip for now
          return undefined;
        }
        return inst;
      })
      .filter((inst): inst is Instrument => inst !== undefined);
    return result;
  }, [watchlistGroups, instruments]);




  const handleGroupSelect = (groupId: string) => {
    setGroup(groupId);
  };

  const handleStartEditGroup = (e: React.MouseEvent, groupId: string, currentName: string) => {
    e.stopPropagation();
    setEditingGroupId(groupId);
    setEditingGroupName(currentName);
  };

  const handleSaveGroupName = async (groupId: string) => {
    if (editingGroupName.trim()) {
      try {
        await editGroupName(groupId, editingGroupName.trim());
        setEditingGroupId(null);
        setEditingGroupName('');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update group name:', error);
        // Show error to user (you can add a toast notification here)
      }
    }
  };

  const handleCancelEditGroup = () => {
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  const handleDeleteGroup = async (e: React.MouseEvent, groupId: string) => {
    e.stopPropagation();
    // Prevent deleting if it's the last group
    if (groups.length <= 1) {
      return;
    }

    // Prevent deleting default group (should not happen due to UI, but double-check)
    if (watchlistGroups[groupId]?.is_default) {
      return;
    }

    const groupToDelete = groups.find(g => g.id === groupId);

    // Confirm deletion
    if (window.confirm(`Are you sure you want to delete "${groupToDelete?.name}"? This will remove all instruments in this group.`)) {
      try {
        await deleteGroup(groupId);
        // Reload groups to refresh the list
        await loadGroups();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete group:', error);
        // Show error to user (you can add a toast notification here)
      }
    }
  };

  const handleStartCreateGroup = () => {
    setIsCreatingGroup(true);
    setNewGroupName('');
  };

  const handleSaveNewGroup = async () => {
    if (newGroupName.trim()) {
      try {
        await createGroup(newGroupName.trim());
        setIsCreatingGroup(false);
        setNewGroupName('');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create group:', error);
        // Show error to user (you can add a toast notification here)
      }
    }
  };

  const handleCancelCreateGroup = () => {
    setIsCreatingGroup(false);
    setNewGroupName('');
  };

  const handleToggleWatchlist = async (e: React.MouseEvent, instrumentId: string) => {
    e.stopPropagation();

    if (isInWatchlist(instrumentId)) {
      setRemovingInstrumentId(instrumentId);
      try {
        await removeInstrument(instrumentId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to remove instrument from watchlist:', error);
        // Show error to user (you can add a toast notification here)
      } finally {
        setRemovingInstrumentId(null);
      }
    } else {
      // If no groups exist, the backend will create a default group
      // Just try to add - the hook will handle finding/creating default group
      if (groups.length === 0) {
        // Reload groups first to get the default group created by backend
        try {
          await loadGroups();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to load groups:', error);
        }
      }

      // Find default group if no current group is selected
      if (!currentGroup && groups.length > 0) {
        const defaultGroup = groups.find(g => (watchlistGroups[g.id]?.is_default));
        if (defaultGroup) {
          setGroup(defaultGroup.id);
        } else {
          setGroup(groups[0].id);
        }
      }

      setAddingInstrumentId(instrumentId);
      try {
        await addInstrument(instrumentId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to add instrument to watchlist:', error);
        // Show error to user (you can add a toast notification here)
      } finally {
        setAddingInstrumentId(null);
      }
    }
  };

  const handleInstrumentClick = (instrument: Instrument) => {
    if (onInstrumentSelect) {
      onInstrumentSelect(instrument);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, instrument: Instrument) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: e.clientX + 2,
          mouseY: e.clientY - 6,
          instrument,
        }
        : null,
    );
  };

  const handleCloseContextMenu = (_event?: React.MouseEvent | {}, _reason?: "backdropClick" | "escapeKeyDown") => {
    setContextMenu(null);
  };

  const handleAnalyzeWithAI = (instrument: Instrument | null) => {
    if (!instrument) return;

    handleCloseContextMenu();

    // Navigate to AI Assist page with instrument data in URL params
    const symbolText = instrument.exchange
      ? `${instrument.symbol} (${instrument.exchange})`
      : instrument.symbol;

    navigate(`/ai-assist?instrument=${encodeURIComponent(symbolText)}&instrumentId=${encodeURIComponent(instrument.instrument_id)}`);
  };



  const handleBuyClick = (e: React.MouseEvent, instrument: Instrument) => {
    e.stopPropagation();
    setSelectedInstrument(instrument);
    setOrderSide('buy');
    setOrderDialogOpen(true);
  };

  const handleSellClick = (e: React.MouseEvent, instrument: Instrument) => {
    e.stopPropagation();
    setSelectedInstrument(instrument);
    setOrderSide('sell');
    setOrderDialogOpen(true);
  };

  const getQuote = (instrumentId: string) => {
    const instrument = instruments[instrumentId];
    if (!instrument || !instrument.broker_instrument_token) {
      console.log(`[getQuote] Failed for ${instrumentId}: missing instrument or token`, instrument);
      return null;
    }

    const quote = liveQuotes[instrument.broker_instrument_token] || null;
    console.log(`[getQuote] Lookup for ${instrument.symbol} (${instrument.broker_instrument_token}):`, quote);
    return quote;
  };

  // Find all visible instrument tokens to subscribe to market data
  const visibleTokens = useMemo(() => {
    const tokens = new Set<string>();

    // 1. Add tokens from expanded watchlist groups
    Object.keys(expandedGroups).forEach(groupId => {
      if (expandedGroups[groupId]) {
        const groupInsts = getGroupInstruments(groupId);
        groupInsts.forEach(inst => {
          if (inst.broker_instrument_token) {
            tokens.add(inst.broker_instrument_token);
          }
        });
      }
    });

    // 2. Add tokens from paginated search results which are currently visible
    if (searchQuery.trim() && results && results.length > 0) {
      // Create paginated logic replicated inline or use a separate effect
      const filtered = results.filter((instrument) => {
        try { return !isInWatchlist(instrument.instrument_id); } catch (e) { return true; }
      });
      const paginated = filtered.length <= ITEMS_PER_PAGE ? filtered :
        filtered.slice((searchPage - 1) * ITEMS_PER_PAGE, ((searchPage - 1) * ITEMS_PER_PAGE) + ITEMS_PER_PAGE);

      paginated.forEach(inst => {
        if (inst.broker_instrument_token) {
          tokens.add(inst.broker_instrument_token);
        }
      });
    }

    // 3. Add tokens from current group if on WATCHLIST tab
    if (activeTab === 'WATCHLIST' && currentGroup) {
      const groupInsts = getGroupInstruments(currentGroup);
      groupInsts.forEach(inst => {
        if (inst.broker_instrument_token) {
          tokens.add(inst.broker_instrument_token);
        }
      });
    }

    // 4. Add tokens from loaded indices
    if (activeTab === 'NIFTY50') {
      nifty50Constituents.forEach(inst => {
        if (inst.broker_instrument_token) {
          tokens.add(inst.broker_instrument_token);
        }
      });
    } else if (activeTab === 'BANKNIFTY') {
      bankniftyConstituents.forEach(inst => {
        if (inst.broker_instrument_token) {
          tokens.add(inst.broker_instrument_token);
        }
      });
    }

    console.log('[InstrumentSidebar] Recalculating visibleTokens', {
      expandedGroupsCount: Object.keys(expandedGroups).filter(k => expandedGroups[k]).length,
      searchQuery,
      resultsLength: results?.length,
      currentGroup,
      activeTab
    });

    return Array.from(tokens);
  }, [expandedGroups, getGroupInstruments, searchQuery, results, isInWatchlist, searchPage, currentGroup, activeTab, nifty50Constituents, bankniftyConstituents]);

  // Handle auto-subscribing / unsubscribing via WebSocket payload
  useMarketDataSubscription(visibleTokens);

  // Filter search results to exclude instruments already in watchlist
  const filteredSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    if (!results || results.length === 0) return [];

    try {
      return results.filter((instrument) => {
        try {
          return !isInWatchlist(instrument.instrument_id);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error checking if instrument is in watchlist:', error);
          return true; // Show instrument if there's an error
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error filtering search results:', error);
      return results; // Return all results if filtering fails
    }
  }, [searchQuery, results, isInWatchlist]);

  // Paginate search results
  const paginatedSearchResults = useMemo(() => {
    if (filteredSearchResults.length <= ITEMS_PER_PAGE) {
      return filteredSearchResults;
    }
    const startIndex = (searchPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredSearchResults.slice(startIndex, endIndex);
  }, [filteredSearchResults, searchPage]);

  const totalPages = Math.ceil(filteredSearchResults.length / ITEMS_PER_PAGE);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setSearchPage(value);
    // Scroll to top of search results when page changes
    const searchResultsContainer = document.querySelector('[data-search-results]');
    if (searchResultsContainer) {
      searchResultsContainer.scrollTop = 0;
    }
  };

  return (
    <Card
      sx={{
        width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgba(4, 55, 123, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        boxShadow: `
          0 0 0 1px rgba(4, 55, 123, 0.15),
          0 0 8px rgba(4, 55, 123, 0.2),
          0 0 15px rgba(4, 55, 123, 0.15),
          inset 0 0 10px rgba(255, 255, 255, 0.03),
          inset 0 1px 0 rgba(255, 255, 255, 0.1),
          2px 0 10px rgba(4, 55, 123, 0.15),
          -2px 0 10px rgba(4, 55, 123, 0.1)
        `,
        position: 'relative',
      }}
    >
      {/* Segment Filter (Above Search) */}
      <Box sx={{ px: 2, pt: 1, pb: 0, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={segmentFilter}
          exclusive
          onChange={handleSegmentChange}
          size="small"
          sx={{
            width: '100%',
            height: '24px',
            backgroundColor: 'rgba(4, 55, 123, 0.05)',
            border: '1px solid rgba(4, 55, 123, 0.15)',
            '& .MuiToggleButton-root': {
              flex: 1,
              px: 1.5,
              fontSize: '0.65rem',
              fontWeight: 600,
              color: 'rgba(4, 55, 123, 0.6)',
              border: 'none',
              borderRadius: '4px',
              '&.Mui-selected': {
                backgroundColor: '#04377b',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#032e64',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(4, 55, 123, 0.1)',
              },
            },
          }}
        >
          <Tooltip title="Equities" arrow placement="top">
            <ToggleButton value="EQ">EQ</ToggleButton>
          </Tooltip>
          <Tooltip title="Futures & Options" arrow placement="top">
            <ToggleButton value="FO">FO</ToggleButton>
          </Tooltip>
          <Tooltip title="Currency Derivatives" arrow placement="top">
            <ToggleButton value="CDS">CDS</ToggleButton>
          </Tooltip>
          <Tooltip title="Commodities" arrow placement="top">
            <ToggleButton value="COM">COM</ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>


      {/* Search Bar */}
      <Box sx={{ p: 2, pt: 1, borderBottom: 1, borderColor: 'rgba(0, 0, 0, 0.12)', backgroundColor: 'transparent' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search eg: infy bse, nifty fut, index fund, etc"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleClearSearch();
          }}
          inputRef={searchInputRef}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.75rem',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
              '& input': {
                fontSize: '0.75rem',
                padding: '8px 14px',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 0.5 }}>
                {searchQuery && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{
                      p: 0.25,
                      color: 'text.secondary',
                      '&:hover': { color: 'error.main', backgroundColor: 'rgba(244, 67, 54, 0.08)' }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                )}
                <Chip
                  label="Ctrl + K"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    opacity: searchQuery ? 0.5 : 0.8,
                    transition: 'opacity 0.2s'
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabs (Below Search) */}
      {!searchQuery.trim() && (
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(0, 0, 0, 0.12)' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              '& .MuiTabs-indicator': { height: 2 },
              '& .MuiTab-root': {
                minHeight: 36,
                fontSize: '0.7rem',
                textTransform: 'none',
                fontWeight: 'medium',
                py: 0,
                color: 'text.secondary',
                '&.Mui-selected': { color: '#04377b' },
              }
            }}
          >
            <Tab label="NIFTY 50" value="NIFTY50" />
            <Tab label="BANKNIFTY" value="BANKNIFTY" />
            <Tab label="WATCHLIST" value="WATCHLIST" />
          </Tabs>
          <Box sx={{
            height: 2,
            bgcolor: '#04377b',
            width: activeTab === 'NIFTY50' ? '33.33%' : activeTab === 'BANKNIFTY' ? '66.66%' : '100%',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'absolute',
            bottom: 0,
            left: activeTab === 'NIFTY50' ? '0%' : activeTab === 'BANKNIFTY' ? '33.33%' : '66.66%',
            zIndex: 1
          }} />
        </Box>
      )}

      {/* Content Section */}
      {!searchQuery.trim() && (
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(0, 0, 0, 0.12)', backgroundColor: 'transparent', flex: 1, overflow: 'auto' }}>
          {activeTab === 'NIFTY50' && (
            <>
              {indicesLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {indicesError && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" color="error">{indicesError}</Typography>
                </Box>
              )}
              {!indicesLoading && nifty50Constituents.length === 0 && !indicesError && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">NIFTY 50 Constituents</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>No data available</Typography>
                </Box>
              )}
              {!indicesLoading && nifty50Constituents.length > 0 && (
                <Box sx={{ pl: 2, pr: 1, pb: 0.5 }}>
                  <List dense sx={{ py: 0 }}>
                    {nifty50Constituents.map((instrument) => {
                      const quote = getQuote(instrument.instrument_id);
                      const dayChange = quote?.change ?? 0;
                      const dayChangePercent = quote?.change_percentage ?? 0;
                      const ltp = quote?.last_price;
                      const isPositive = dayChange >= 0;
                      const instrumentInWatchlist = (() => { try { return isInWatchlist(instrument.instrument_id); } catch (e) { return false; } })();

                      return (
                        <DraggableInstrumentItem key={instrument.instrument_id} instrument={instrument}>
                          <ListItem
                            disablePadding
                            onContextMenu={(e) => handleContextMenu(e, instrument)}
                            sx={{
                              borderBottom: 1,
                              borderColor: 'rgba(0, 0, 0, 0.08)',
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .action-buttons': {
                                  display: 'flex',
                                },
                              },
                              ...((menuInstrument?.instrument_id === instrument.instrument_id) && {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .action-buttons': {
                                  display: 'flex',
                                },
                              })
                            }}
                          >
                            <ListItemButton
                              onClick={() => handleInstrumentClick(instrument)}
                              sx={{
                                py: 0.25,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                              }}
                            >
                              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
                                    {instrument.symbol}
                                    {instrument.exchange && (
                                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', ml: 0.5, mr: 0.5 }}>
                                        {' | '}
                                        {instrument.exchange}
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1 }}>
                                  {ltp !== undefined ? (
                                    <>
                                      <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                                        {formatCurrency(ltp, { decimals: 2 })}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color={isPositive ? 'success.main' : 'error.main'}
                                        sx={{ display: 'flex', gap: 0.5, fontSize: '0.65rem' }}
                                      >
                                        {formatCurrency(dayChange, { showSign: true, decimals: 2 })}
                                        {' '}
                                        ({formatPercentage(dayChangePercent, 2, true)})
                                      </Typography>
                                    </>
                                  ) : null}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
                                  <Box
                                    className="action-buttons"
                                    sx={{
                                      display: 'none',
                                      alignItems: 'center',
                                      gap: 0.25,
                                    }}
                                  >
                                    <Tooltip title="Buy" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => handleBuyClick(e, instrument)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(76, 175, 80, 0.5)',
                                          color: 'rgba(76, 175, 80, 0.9)',
                                          backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(76, 175, 80, 0.7)',
                                            backgroundColor: 'rgba(76, 175, 80, 0.12)',
                                          },
                                        }}
                                      >
                                        B
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Sell" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => handleSellClick(e, instrument)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(244, 67, 54, 0.5)',
                                          color: 'rgba(244, 67, 54, 0.9)',
                                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(244, 67, 54, 0.7)',
                                            backgroundColor: 'rgba(244, 67, 54, 0.12)',
                                          },
                                        }}
                                      >
                                        S
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Analyze with AI" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); handleAnalyzeWithAI(instrument); }}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(4, 55, 123, 0.5)',
                                          color: 'rgba(4, 55, 123, 0.9)',
                                          backgroundColor: 'rgba(4, 55, 123, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(4, 55, 123, 0.7)',
                                            backgroundColor: 'rgba(4, 55, 123, 0.12)',
                                          },
                                        }}
                                      >
                                        A
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title={instrumentInWatchlist ? "Watchlist Options" : "Add to Watchlist"} arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={instrumentInWatchlist ? (e) => handleOpenItemMenu(e, instrument) : (e) => handleToggleWatchlist(e, instrument.instrument_id)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.5)' : 'rgba(76, 175, 80, 0.5)',
                                          color: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.9)' : 'rgba(76, 175, 80, 0.9)',
                                          backgroundColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.08)' : 'rgba(76, 175, 80, 0.08)',
                                          '&:hover': {
                                            borderColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.7)' : 'rgba(76, 175, 80, 0.7)',
                                            backgroundColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.12)' : 'rgba(76, 175, 80, 0.12)',
                                          },
                                        }}
                                      >
                                        {instrumentInWatchlist ? '-' : '+'}
                                      </Button>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        </DraggableInstrumentItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </>
          )}
          {activeTab === 'BANKNIFTY' && (
            <>
              {indicesLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {indicesError && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" color="error">{indicesError}</Typography>
                </Box>
              )}
              {!indicesLoading && bankniftyConstituents.length === 0 && !indicesError && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">BANKNIFTY Constituents</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>No data available</Typography>
                </Box>
              )}
              {!indicesLoading && bankniftyConstituents.length > 0 && (
                <Box sx={{ pl: 2, pr: 1, pb: 0.5 }}>
                  <List dense sx={{ py: 0 }}>
                    {bankniftyConstituents.map((instrument) => {
                      const quote = getQuote(instrument.instrument_id);
                      const dayChange = quote?.change ?? 0;
                      const dayChangePercent = quote?.change_percentage ?? 0;
                      const ltp = quote?.last_price;
                      const isPositive = dayChange >= 0;
                      const instrumentInWatchlist = (() => { try { return isInWatchlist(instrument.instrument_id); } catch (e) { return false; } })();

                      return (
                        <DraggableInstrumentItem key={instrument.instrument_id} instrument={instrument}>
                          <ListItem
                            disablePadding
                            onContextMenu={(e) => handleContextMenu(e, instrument)}
                            sx={{
                              borderBottom: 1,
                              borderColor: 'rgba(0, 0, 0, 0.08)',
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .action-buttons': {
                                  display: 'flex',
                                },
                              },
                              ...((menuInstrument?.instrument_id === instrument.instrument_id) && {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .action-buttons': {
                                  display: 'flex',
                                },
                              })
                            }}
                          >
                            <ListItemButton
                              onClick={() => handleInstrumentClick(instrument)}
                              sx={{
                                py: 0.25,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                              }}
                            >
                              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
                                    {instrument.symbol}
                                    {instrument.exchange && (
                                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', ml: 0.5, mr: 0.5 }}>
                                        {' | '}
                                        {instrument.exchange}
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1 }}>
                                  {ltp !== undefined ? (
                                    <>
                                      <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                                        {formatCurrency(ltp, { decimals: 2 })}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color={isPositive ? 'success.main' : 'error.main'}
                                        sx={{ display: 'flex', gap: 0.5, fontSize: '0.65rem' }}
                                      >
                                        {formatCurrency(dayChange, { showSign: true, decimals: 2 })}
                                        {' '}
                                        ({formatPercentage(dayChangePercent, 2, true)})
                                      </Typography>
                                    </>
                                  ) : null}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
                                  <Box
                                    className="action-buttons"
                                    sx={{
                                      display: 'none',
                                      alignItems: 'center',
                                      gap: 0.25,
                                    }}
                                  >
                                    <Tooltip title="Buy" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => handleBuyClick(e, instrument)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(76, 175, 80, 0.5)',
                                          color: 'rgba(76, 175, 80, 0.9)',
                                          backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(76, 175, 80, 0.7)',
                                            backgroundColor: 'rgba(76, 175, 80, 0.12)',
                                          },
                                        }}
                                      >
                                        B
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Sell" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => handleSellClick(e, instrument)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(244, 67, 54, 0.5)',
                                          color: 'rgba(244, 67, 54, 0.9)',
                                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(244, 67, 54, 0.7)',
                                            backgroundColor: 'rgba(244, 67, 54, 0.12)',
                                          },
                                        }}
                                      >
                                        S
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Analyze with AI" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); handleAnalyzeWithAI(instrument); }}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(4, 55, 123, 0.5)',
                                          color: 'rgba(4, 55, 123, 0.9)',
                                          backgroundColor: 'rgba(4, 55, 123, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(4, 55, 123, 0.7)',
                                            backgroundColor: 'rgba(4, 55, 123, 0.12)',
                                          },
                                        }}
                                      >
                                        A
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title={instrumentInWatchlist ? "Watchlist Options" : "Add to Watchlist"} arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={instrumentInWatchlist ? (e) => handleOpenItemMenu(e, instrument) : (e) => handleToggleWatchlist(e, instrument.instrument_id)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.5)' : 'rgba(76, 175, 80, 0.5)',
                                          color: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.9)' : 'rgba(76, 175, 80, 0.9)',
                                          backgroundColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.08)' : 'rgba(76, 175, 80, 0.08)',
                                          '&:hover': {
                                            borderColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.7)' : 'rgba(76, 175, 80, 0.7)',
                                            backgroundColor: instrumentInWatchlist ? 'rgba(244, 67, 54, 0.12)' : 'rgba(76, 175, 80, 0.12)',
                                          },
                                        }}
                                      >
                                        {instrumentInWatchlist ? '-' : '+'}
                                      </Button>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        </DraggableInstrumentItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </>
          )}
          {activeTab === 'WATCHLIST' && (
            <>
              <Box sx={{ p: 1.5, pb: 1, borderBottom: 1, borderColor: 'rgba(0, 0, 0, 0.08)' }}>
                {isCreatingGroup ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder="Enter group name"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveNewGroup();
                        else if (e.key === 'Escape') handleCancelCreateGroup();
                      }}
                      autoFocus
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-root': { fontSize: '0.875rem', height: '32px' },
                      }}
                    />
                    <IconButton size="small" onClick={handleSaveNewGroup} disabled={!newGroupName.trim()} sx={{ color: 'success.main' }}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelCreateGroup} sx={{ color: 'error.main' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : editingGroupId === currentGroup ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      size="small"
                      value={editingGroupName}
                      onChange={(e) => setEditingGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveGroupName(currentGroup!);
                        else if (e.key === 'Escape') handleCancelEditGroup();
                      }}
                      autoFocus
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-root': { fontSize: '0.875rem', height: '32px' },
                      }}
                    />
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSaveGroupName(currentGroup!); }} sx={{ color: 'success.main' }}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCancelEditGroup(); }} sx={{ color: 'error.main' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={currentGroup || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === 'CREATE_NEW') {
                            handleStartCreateGroup();
                          } else {
                            handleGroupSelect(val);
                          }
                        }}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <ListAltIcon fontSize="small" sx={{ color: '#04377b', ml: 0.5 }} />
                          </InputAdornment>
                        }
                        sx={{
                          fontSize: '0.8rem',
                          height: '36px',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '6px',
                          '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center' },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(4, 55, 123, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#04377b',
                          },
                        }}
                      >
                        {groups.length === 0 && (
                          <MenuItem disabled value="" sx={{ fontSize: '0.8rem' }}>
                            No groups available
                          </MenuItem>
                        )}
                        {groups.map((groupItem) => (
                          <MenuItem key={groupItem.id} value={groupItem.id} sx={{ fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between' }}>
                            <span>{groupItem.name}</span> <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>({groupItem.count})</Typography>
                          </MenuItem>
                        ))}
                        <Divider />
                        <MenuItem value="CREATE_NEW" sx={{ fontSize: '0.8rem', color: '#04377b', mt: 0.5 }}>
                          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Create New Watchlist
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {currentGroup && groups.length > 1 && !watchlistGroups[currentGroup]?.is_default && (
                        <Tooltip title="Delete Group">
                          <IconButton size="small" onClick={(e) => handleDeleteGroup(e, currentGroup)} sx={{ opacity: 0.7, '&:hover': { opacity: 1, color: 'error.main' } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {currentGroup && (
                        <Tooltip title="Rename Group">
                          <IconButton size="small" onClick={(e) => handleStartEditGroup(e, currentGroup, groups.find(g => g.id === currentGroup)?.name || '')} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              {watchlistError && (
                <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                  <Typography variant="caption" color="error" sx={{ display: 'block', fontSize: '0.7rem' }}>
                    {watchlistError}
                  </Typography>
                </Box>
              )}
              {watchlistLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {groups.length === 0 && !isCreatingGroup && (
                <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                    No watchlist groups
                  </Typography>
                </Box>
              )}

              {currentGroup && getGroupInstruments(currentGroup).length > 0 && (
                <Box sx={{ pl: 2, pr: 1, pb: 0.5 }}>
                  <List dense sx={{ py: 0 }}>
                    {getGroupInstruments(currentGroup).map((instrument) => {
                      const quote = getQuote(instrument.instrument_id);
                      const dayChange = quote?.change ?? 0;
                      const dayChangePercent = quote?.change_percentage ?? 0;
                      const ltp = quote?.last_price;
                      const isPositive = dayChange >= 0;

                      return (
                        <DraggableInstrumentItem key={instrument.instrument_id} instrument={instrument}>
                          <ListItem
                            disablePadding
                            onContextMenu={(e) => handleContextMenu(e, instrument)}
                            sx={{
                              borderBottom: 1,
                              borderColor: 'rgba(0, 0, 0, 0.08)',
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .action-buttons': {
                                  display: 'flex',
                                },
                              },
                              ...((menuInstrument?.instrument_id === instrument.instrument_id) && {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .action-buttons': {
                                  display: 'flex',
                                },
                              })
                            }}
                          >
                            <ListItemButton
                              onClick={() => handleInstrumentClick(instrument)}
                              sx={{
                                py: 0.25,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                              }}
                            >
                              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
                                    {instrument.symbol}
                                    {instrument.exchange && (
                                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', ml: 0.5, mr: 0.5 }}>
                                        {' | '}
                                        {instrument.exchange}
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1 }}>
                                  {ltp !== undefined ? (
                                    <>
                                      <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                                        {formatCurrency(ltp, { decimals: 2 })}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color={isPositive ? 'success.main' : 'error.main'}
                                        sx={{ display: 'flex', gap: 0.5, fontSize: '0.65rem' }}
                                      >
                                        {formatCurrency(dayChange, { showSign: true, decimals: 2 })}
                                        {' '}
                                        ({formatPercentage(dayChangePercent, 2, true)})
                                      </Typography>
                                    </>
                                  ) : null}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
                                  <Box
                                    className="action-buttons"
                                    sx={{
                                      display: 'none',
                                      alignItems: 'center',
                                      gap: 0.25,
                                    }}
                                  >
                                    <Tooltip title="Buy" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => handleBuyClick(e, instrument)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(76, 175, 80, 0.5)',
                                          color: 'rgba(76, 175, 80, 0.9)',
                                          backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(76, 175, 80, 0.7)',
                                            backgroundColor: 'rgba(76, 175, 80, 0.12)',
                                          },
                                        }}
                                      >
                                        B
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Sell" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => handleSellClick(e, instrument)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(244, 67, 54, 0.5)',
                                          color: 'rgba(244, 67, 54, 0.9)',
                                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(244, 67, 54, 0.7)',
                                            backgroundColor: 'rgba(244, 67, 54, 0.12)',
                                          },
                                        }}
                                      >
                                        S
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Analyze with AI" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); handleAnalyzeWithAI(instrument); }}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(4, 55, 123, 0.5)',
                                          color: 'rgba(4, 55, 123, 0.9)',
                                          backgroundColor: 'rgba(4, 55, 123, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(4, 55, 123, 0.7)',
                                            backgroundColor: 'rgba(4, 55, 123, 0.12)',
                                          },
                                        }}
                                      >
                                        A
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Watchlist Options" arrow placement="top">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => handleOpenItemMenu(e, instrument)}
                                        sx={{
                                          minWidth: 'auto',
                                          px: 0.75,
                                          py: 0.25,
                                          fontSize: '0.65rem',
                                          textTransform: 'none',
                                          height: '20px',
                                          borderRadius: '4px',
                                          borderColor: 'rgba(244, 67, 54, 0.5)',
                                          color: 'rgba(244, 67, 54, 0.9)',
                                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                          '&:hover': {
                                            borderColor: 'rgba(244, 67, 54, 0.7)',
                                            backgroundColor: 'rgba(244, 67, 54, 0.12)',
                                          },
                                        }}
                                      >
                                        -
                                      </Button>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        </DraggableInstrumentItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {currentGroup && getGroupInstruments(currentGroup).length === 0 && !watchlistLoading && (
                <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    No instruments in this group
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.65rem' }}>
                    Search for an instrument above and click + to add
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Search Results Section - shown when searching */}
      {searchQuery.trim() && (
        <Box
          sx={{ flex: 1, borderTop: 1, borderColor: 'rgba(0, 0, 0, 0.12)', display: 'flex', flexDirection: 'column', minHeight: 0 }}
          data-search-results
        >
          {error && (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="error" sx={{ mb: 1, fontSize: '0.75rem' }}>
                Error: {error}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Please check your connection and try again.
              </Typography>
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && !error && results.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                No instruments found for &quot;{searchQuery}&quot;.
              </Typography>
            </Box>
          )}

          {!loading && !error && results.length > 0 && filteredSearchResults.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                All search results are already in your watchlist.
              </Typography>
            </Box>
          )}

          {!loading && !error && filteredSearchResults.length > 0 && (
            <>
              <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                <List dense sx={{ py: 0 }}>
                  {paginatedSearchResults.map((instrument) => {
                    const quote = getQuote(instrument.instrument_id);
                    const dayChange = quote?.change ?? 0;
                    const dayChangePercent = quote?.change_percentage ?? 0;
                    const ltp = quote?.last_price;
                    const isPositive = dayChange >= 0;

                    return (
                      <DraggableInstrumentItem key={instrument.instrument_id} instrument={instrument}>
                        <ListItem
                          disablePadding
                          onContextMenu={(e) => handleContextMenu(e, instrument)}
                          sx={{
                            borderBottom: 1,
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.15)',
                              '& .action-buttons': {
                                display: 'flex',
                              },
                            },
                            ...((menuInstrument?.instrument_id === instrument.instrument_id) && {
                              backgroundColor: 'rgba(255, 255, 255, 0.15)',
                              '& .action-buttons': {
                                display: 'flex',
                              },
                            })
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleInstrumentClick(instrument)}
                            sx={{
                              py: 0.25,
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              },
                            }}
                          >
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
                                  {instrument.symbol}
                                  {instrument.exchange && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', ml: 0.5, mr: 0.5 }}>
                                      {' | '}
                                      {instrument.exchange}
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1 }}>
                                {ltp !== undefined ? (
                                  <>
                                    <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                                      {formatCurrency(ltp, { decimals: 2 })}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color={isPositive ? 'success.main' : 'error.main'}
                                      sx={{ display: 'flex', gap: 0.5, fontSize: '0.65rem' }}
                                    >
                                      {formatCurrency(dayChange, { showSign: true, decimals: 2 })}
                                      {' '}
                                      ({formatPercentage(dayChangePercent, 2, true)})
                                    </Typography>
                                  </>
                                ) : null}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
                                <Box
                                  className="action-buttons"
                                  sx={{
                                    display: 'none',
                                    alignItems: 'center',
                                    gap: 0.25,
                                  }}
                                >
                                  <Tooltip title="Buy" arrow placement="top">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => handleBuyClick(e, instrument)}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 0.75,
                                        py: 0.25,
                                        fontSize: '0.65rem',
                                        textTransform: 'none',
                                        height: '20px',
                                        borderRadius: '4px',
                                        borderColor: 'rgba(76, 175, 80, 0.5)',
                                        color: 'rgba(76, 175, 80, 0.9)',
                                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                        '&:hover': {
                                          borderColor: 'rgba(76, 175, 80, 0.7)',
                                          backgroundColor: 'rgba(76, 175, 80, 0.12)',
                                        },
                                      }}
                                    >
                                      B
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Sell" arrow placement="top">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => handleSellClick(e, instrument)}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 0.75,
                                        py: 0.25,
                                        fontSize: '0.65rem',
                                        textTransform: 'none',
                                        height: '20px',
                                        borderRadius: '4px',
                                        borderColor: 'rgba(244, 67, 54, 0.5)',
                                        color: 'rgba(244, 67, 54, 0.9)',
                                        backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                        '&:hover': {
                                          borderColor: 'rgba(244, 67, 54, 0.7)',
                                          backgroundColor: 'rgba(244, 67, 54, 0.12)',
                                        },
                                      }}
                                    >
                                      S
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Analyze with AI" arrow placement="top">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => { e.stopPropagation(); handleAnalyzeWithAI(instrument); }}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 0.75,
                                        py: 0.25,
                                        fontSize: '0.65rem',
                                        textTransform: 'none',
                                        height: '20px',
                                        borderRadius: '4px',
                                        borderColor: 'rgba(4, 55, 123, 0.5)',
                                        color: 'rgba(4, 55, 123, 0.9)',
                                        backgroundColor: 'rgba(4, 55, 123, 0.08)',
                                        '&:hover': {
                                          borderColor: 'rgba(4, 55, 123, 0.7)',
                                          backgroundColor: 'rgba(4, 55, 123, 0.12)',
                                        },
                                      }}
                                    >
                                      A
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Watchlist Options" arrow placement="top">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => handleOpenItemMenu(e, instrument)}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 0.75,
                                        py: 0.25,
                                        fontSize: '0.65rem',
                                        textTransform: 'none',
                                        height: '20px',
                                        borderRadius: '4px',
                                        borderColor: 'rgba(76, 175, 80, 0.5)',
                                        color: 'rgba(76, 175, 80, 0.9)',
                                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                        '&:hover': {
                                          borderColor: 'rgba(76, 175, 80, 0.7)',
                                          backgroundColor: 'rgba(76, 175, 80, 0.12)',
                                        },
                                      }}
                                    >
                                      +
                                    </Button>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      </DraggableInstrumentItem>
                    );
                  })}
                </List>
              </Box>
              {filteredSearchResults.length > 0 && (
                <Box
                  sx={{
                    p: 1.5,
                    borderTop: 1,
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Showing {((searchPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(searchPage * ITEMS_PER_PAGE, filteredSearchResults.length)} of {filteredSearchResults.length}
                    </Typography>
                  </Box>
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Pagination
                        count={totalPages}
                        page={searchPage}
                        onChange={handlePageChange}
                        size="small"
                        color="primary"
                        sx={{
                          '& .MuiPaginationItem-root': {
                            fontSize: '0.7rem',
                            minWidth: '28px',
                            height: '28px',
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )
      }
      <QuickOrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        instrument={selectedInstrument}
        side={orderSide}
        currentPrice={selectedInstrument ? (liveQuotes[selectedInstrument.instrument_id]?.last_price) : undefined}
      />

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        MenuListProps={{
          'aria-labelledby': 'context-menu',
          dense: true,
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 1,
            boxShadow: `
              0 0 0 1px rgba(255, 235, 59, 0.3),
              0 0 8px rgba(255, 235, 59, 0.4),
              0 0 16px rgba(255, 235, 59, 0.2),
              0 4px 12px rgba(0, 0, 0, 0.15)
            `,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => handleAnalyzeWithAI(contextMenu?.instrument || null)}
          sx={{
            fontSize: '0.75rem',
            py: 0.25,
            px: 1.5,
            minHeight: '32px',
            '&:hover': {
              backgroundColor: 'rgba(4, 55, 123, 0.08)',
            },
          }}
        >
          <PsychologyIcon sx={{ fontSize: 14, mr: 1, color: '#04377b' }} />
          Analyze with AI Assist
        </MenuItem>
      </Menu>
      {/* Item Options Menu (Search & Watchlist) */}
      <Menu
        anchorEl={itemMenuAnchorEl}
        open={Boolean(itemMenuAnchorEl)}
        onClose={handleCloseItemMenu}
        MenuListProps={{ 'aria-labelledby': 'search-menu', dense: true }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 160,
            p: 1,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          },
          '& .MuiList-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            p: 0,
          }
        }}
      >
        {menuInstrument && isInWatchlist(menuInstrument.instrument_id) ? (
          <MenuItem
            disabled={removingInstrumentId === menuInstrument.instrument_id}
            onClick={async (e) => {
              e.stopPropagation();
              if (menuInstrument) {
                const instId = menuInstrument.instrument_id;
                const sym = menuInstrument.symbol;
                handleCloseItemMenu();
                await handleToggleWatchlist(e, instId);
                setToastMessage(`Removed ${sym} from watchlist`);
              }
            }}
            sx={{
              fontSize: '0.75rem',
              py: 0.75,
              px: 1.5,
              borderRadius: 1,
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              color: 'error.main',
              border: '1px solid',
              borderColor: 'rgba(244, 67, 54, 0.2)',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'error.main',
                color: '#fff',
                borderColor: 'error.main',
              }
            }}
          >
            {removingInstrumentId === menuInstrument.instrument_id ? (
              <CircularProgress size={14} sx={{ mr: 1, color: 'inherit' }} />
            ) : (
              <RemoveCircleOutlineIcon sx={{ fontSize: 14, mr: 1 }} />
            )}
            Remove from Watchlist
          </MenuItem>
        ) : (
          <>
            <ListSubheader
              sx={{
                lineHeight: '20px',
                fontSize: '0.65rem',
                backgroundColor: 'transparent',
                userSelect: 'none',
                px: 0.5,
                mt: -0.5,
              }}
            >
              Add to Watchlist
            </ListSubheader>

            {groups.length === 0 ? (
              <MenuItem disabled sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5, fontStyle: 'italic', borderRadius: 1 }}>
                No available groups
              </MenuItem>
            ) : (
              groups.map((group) => (
                <MenuItem
                  key={group.id}
                  disabled={addingInstrumentId === menuInstrument?.instrument_id}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!menuInstrument) return;

                    const instId = menuInstrument.instrument_id;
                    const gId = group.id;
                    const gName = group.name;

                    handleCloseItemMenu();
                    setAddingInstrumentId(instId);

                    try {
                      await addInstrument(instId, gId);
                      setToastMessage(`Added to ${gName}`);
                    } catch (err) {
                      console.error(err);
                    } finally {
                      setAddingInstrumentId(null);
                    }
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: '#04377b',
                      color: '#fff',
                      borderColor: '#04377b',
                    }
                  }}
                >
                  {group.name}
                </MenuItem>
              ))
            )}
          </>
        )}
      </Menu>

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={3000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Card >
  );
}

